import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageSquare } from "lucide-react";

const CATEGORIES = [
  { key: "general", label: "General feedback" },
  { key: "bug", label: "Something's broken" },
  { key: "feature", label: "Feature request" },
  { key: "billing", label: "Payment / billing" },
  { key: "grievance", label: "Complaint / grievance" },
];

export default function Feedback() {
  const { user } = useAuth();
  const [msg, setMsg] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [category, setCategory] = useState("general");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/feedback", { message: msg, category, email: email || undefined });
      toast.success("Thanks for sharing — we'll read every word.");
      setMsg("");
    } catch (err) {
      toast.error("Could not send. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <div className="flex items-center gap-2 mb-4 text-sm text-forest font-medium">
        <MessageSquare className="w-4 h-4" /> Talk to us
      </div>
      <h1 className="font-heading text-3xl font-semibold text-forest mb-3">Feedback & Grievance</h1>
      <p className="text-muted-foreground mb-6">
        Something not working? A suggestion? A complaint? We read every message and respond within 3 business days.
      </p>

      <Card className="p-6 bg-white border-forest/10">
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Category</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  type="button"
                  key={c.key}
                  onClick={() => setCategory(c.key)}
                  data-testid={`fb-cat-${c.key}`}
                  className={`px-3 py-1.5 rounded-full text-sm border ${
                    category === c.key ? "bg-forest text-bone border-forest" : "border-forest/20 hover:border-forest/40"
                  }`}
                >{c.label}</button>
              ))}
            </div>
          </div>
          {!user && (
            <div>
              <label className="text-sm font-medium mb-1 block">Your email (optional, so we can reply)</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="fb-email" />
            </div>
          )}
          <div>
            <label className="text-sm font-medium mb-1 block">Message</label>
            <Textarea
              required
              minLength={3}
              rows={6}
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              placeholder="Tell us what's on your mind…"
              data-testid="fb-message"
            />
          </div>
          <Button type="submit" disabled={busy} className="bg-forest hover:bg-forest/90 text-bone" data-testid="fb-submit">
            {busy ? "Sending…" : "Send"}
          </Button>
        </form>
      </Card>

      <Card className="p-5 mt-6 bg-bone border-forest/15">
        <p className="font-heading text-sm font-semibold text-forest mb-1">Grievance Officer</p>
        <p className="text-sm text-muted-foreground">
          For unresolved complaints, please contact our Grievance Officer at{" "}
          <a href="mailto:path@truepathcareer.com" className="text-forest underline">path@truepathcareer.com</a>.
          We respond within 15 business days as per IT Rules 2021.
        </p>
      </Card>
    </div>
  );
}
